import { Config } from "./Config";
import {
  Block,
  BlockBoundingBox,
  BlockDate,
  CancerStage,
  Case,
  CaseClientStatus,
  CaseExport,
  CaseInfo,
  CaseReviewStatus,
  CategoryFindingSummary,
  Cell,
  CellAiConfigView,
  CellKey,
  CellType,
  CellValue,
  ClusterType,
  CustomCaseProperty,
  CustomPropertyQuery,
  DistinctMode,
  DocumentStatus,
  FieldAiConfigExamples,
  FieldAiConfigExampleSetInput,
  FieldAiConfigFilters,
  FieldAiConfigInputMode,
  FieldAiConfigModelMode,
  FieldAiConfigView,
  FieldCellValue,
  FieldKey,
  FieldPresence,
  FormField,
  FormFieldValueGroup as FFVG,
  FormFieldValues as FFV,
  FormFieldValues,
  FormSection,
  KeyValueOptionalInput,
  LensHitsFilterParamsInput,
  LensOutcomeStatusConfigurationView,
  LensType,
  Maybe,
  OutcomeStatus,
  OverallOutcomeStatus,
  PageType,
  PendingUserView,
  Permission,
  RuleCategory,
  SegmentMetrics,
  UserSession,
  ValidationStatus,
  KeyedCaseCalcView,
  ProfileExportConfig,
  UserType,
  GetCasesParamsInput,
  SectionReviewState,
  TagView,
  FormAssigneeType,
} from "generated/graphql";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { differenceInDays, format, formatRelative, isSameDay, isValid, parseISO } from "date-fns";
import { Base64 } from "js-base64";
import { redirectToSignin } from "appHistory";
import { parseContentDispositionFilename } from "./helpers/parseContentDisposition";
import { ReactElement } from "react";
import { stripNull } from "helpers/coll";
import { assertNever } from "helpers/utils";

export const ALL_REVIEW_STATUSES: { name: string; value: CaseReviewStatus }[] = Object.entries(CaseReviewStatus).map(
  ([name, value]) => ({
    name: name,
    value: value,
  })
);

interface Success<T> {
  tag: "success";
  value: T;
}

interface Unauthorized {
  tag: "unauthorized";
}

interface ServerError {
  tag: "error";
  error?: string;
}

interface NotFound {
  tag: "not_found";
  error?: string;
}

interface Forbidden {
  tag: "forbidden";
}

export interface NewLens {
  name: string;
  description: string;
  primaryLabel: string;
  secondaryLabel: string;
  tertiaryLabel: string;
  lensType: LensType;
}

export interface KeyedCaseValueCalc extends KeyedCaseCalcView {
  timestamp: string;
  value: string;
}

export type BackendResponse<T> = Success<T> | Unauthorized | ServerError | NotFound | Forbidden;

export type BackendErrorResponse = { message: string; statusCode: number };

export type EditAction<T> =
  | {
      type: "Set";
      value: T;
    }
  | { type: "Clear" }
  | { type: "Ignore" };

export function success<T>(t: T): Success<T> {
  return { tag: "success", value: t };
}

export const unauthorized: Unauthorized = { tag: "unauthorized" };
export const serverError: ServerError = { tag: "error" };
export const forbidden: Forbidden = { tag: "forbidden" };

export const renderFormAssigneeType: (typ: FormAssigneeType) => string = (typ: FormAssigneeType) => {
  if (typ === FormAssigneeType.Review) {
    return "Review";
  } else if (typ === FormAssigneeType.Qa) {
    return "QA";
  } else {
    return assertNever(typ);
  }
};

export interface GetProblemDocumentsRequest {
  getTransfer: boolean;
  getIntake: boolean;
  repairIntake: boolean;
  getTextract: boolean;
  repairTextract: boolean;
  getWits: boolean;
  repairWits: boolean;
  repairFailedPages: boolean;
  getIndex: boolean;
  repairIndex: boolean;
  getLensRun: boolean;
  repairLensRun: boolean;
  getMissingPages: boolean;
  repairMissingPages: boolean;
}

export type FieldId = number;

export function displayUserType(type: UserType): string {
  switch (type) {
    case UserType.Reviewer:
      return "Reviewer";
    case UserType.Admin:
      return "Admin";
  }
}

export interface UserInfo {
  id: number;
  firstName: string;
  lastName: string;
  clientId: number;
  email: string;
  createdAt: string;
  userType: UserType;
}

export interface UserForCredentialResponse {
  users: UserForCredential[];
}

export interface UserForCredential {
  clientId: number;
  clientName: string;
  email: string;
}

export interface CheckOnboardedResponse {
  message: "invalid_token" | "already_onboarded" | "valid";
  pendingUser?: PendingUserView;
}

export interface CredentialsResponse {
  id: number;
  clientId: number;
  userId: number;
  credentialId: string;
  credentialSecret: string;
  createdAt: string;
  lastUsed: string | null;
}

export interface GetUserApiCredentialsResponse {
  credentials: CredentialsResponse[];
}

export interface CreateUserApiCredentialResponse {
  credential: CredentialsResponse;
}

export interface LensRunParams {
  lensId: number;
  caseId: number;
  offset: number;
  limit: number;
  includeSurroundings: boolean;
  filters: LensHitsFilterParamsInput;
}

export function getMessageForAxiosError(error: AxiosError): string {
  let msg: string;
  if (error.response?.data?.["message"]) {
    msg = error.response.data?.["message"];
  } else if (error.status === 500) {
    msg = "Internal Server Error";
  } else {
    msg = error.message;
  }
  return msg;
}

/**
 * The typescript generation from our GQL schema will create typings for arrays as T | T[] instead of
 * strictly T[]. This function just removes the singular T from the typing.
 */
export function forceGQLArray<T>(items: T | T[] | undefined): T[] {
  return isNotNullish(items) ? (items as T[]) : ([] as T[]);
}

export const CLUSTER_TYPES = Object.values(ClusterType);
export { ClusterType };

export function displayClusterType(type: ClusterType): string {
  switch (type) {
    case ClusterType.FamilyHistory:
      return "Family History";
    case ClusterType.PatientHistory:
      return "Patient History";
    case ClusterType.PatientEducation:
      return "Patient Education";
    case ClusterType.AuthorizationForm:
      return "Authorization Form";
    case ClusterType.LabResult:
      return "Lab Result";
    case ClusterType.Header:
      return "Header Section";
    case ClusterType.CheckboxForm:
      return "Checkbox Form";
    case ClusterType.DeviceLabel:
      return "Device Label";
    case ClusterType.CurrentMedications:
      return "Current Medications";
    case ClusterType.VisitReason:
      return "Visit Reason";
    case ClusterType.SocialHistory:
      return "Social History";
    case ClusterType.SurgicalHistory:
      return "Surgical History";
    case ClusterType.HistoryPresentIllness:
      return "HPI / Problem List";
  }
}

export type BlockDateType = "year" | "month" | "day";

export function getDateType(blockDate: BlockDate): BlockDateType {
  const month = blockDate.monthDay;
  if (month) {
    const day = month.day;
    if (day) {
      return "day";
    } else {
      return "month";
    }
  } else {
    return "year";
  }
}

export interface Lens {
  id: number;
  name: string;
  description: string;
  formId: number;
  version: number;
  createdAt: string;
  ruleIdSerial: number;
  draftVersion?: number;
  litigationName: string;
}

/**
 * Returns the start of the range the given block date represents
 */
export function parseBlockDate(blockDate: BlockDate): Date {
  const month = blockDate.monthDay;
  let result: Date;
  if (month) {
    const day = month.day;
    if (day) {
      result = new Date(blockDate.year, month.month - 1, day);
    } else {
      result = new Date(blockDate.year, month.month - 1, 1);
    }
  } else {
    result = new Date(blockDate.year, 0, 1);
  }
  return result;
}

export function monthName(month: number): string {
  switch (month) {
    case 1:
      return "January";
    case 2:
      return "February";
    case 3:
      return "March";
    case 4:
      return "April";
    case 5:
      return "May";
    case 6:
      return "June";
    case 7:
      return "July";
    case 8:
      return "August";
    case 9:
      return "September";
    case 10:
      return "October";
    case 11:
      return "November";
    case 12:
      return "December";
    default:
      return "";
  }
}

export function displayBlockDate(blockDate?: BlockDate | null): string {
  if (!blockDate) return "";
  const monthDay = blockDate.monthDay;
  if (monthDay) {
    const month = monthName(monthDay.month);
    const day = monthDay.day;
    if (day) {
      return `${month} ${day} ${blockDate.year}`;
    } else {
      return `${month} ${blockDate.year}`;
    }
  } else {
    return blockDate.year.toString();
  }
}

export function blockDateFromDate(date: Date): BlockDate {
  return {
    year: date.getUTCFullYear(),
    monthDay: {
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    },
  };
}

export class BlockDateModel {
  public readonly blockDate: BlockDate;

  constructor(input: Date | BlockDate) {
    if ((input as BlockDate).year !== undefined) {
      this.blockDate = input as BlockDate;
    } else {
      this.blockDate = blockDateFromDate(input as Date);
    }
  }

  year(): number {
    return this.blockDate.year;
  }

  month(): number | undefined {
    return this.blockDate.monthDay?.month;
  }

  day(): number | undefined | null {
    return this.blockDate.monthDay?.day;
  }

  type(): BlockDateType {
    return getDateType(this.blockDate);
  }

  display(): string {
    return displayBlockDate(this.blockDate);
  }

  date(): Date {
    return parseBlockDate(this.blockDate);
  }

  clearDay(): BlockDateModel {
    const month = this.month();
    if (month) {
      return new BlockDateModel({
        year: this.year(),
        monthDay: {
          month: month,
          day: undefined,
        },
      });
    } else {
      return this;
    }
  }

  clearMonth(): BlockDateModel {
    return new BlockDateModel({ year: this.year() });
  }
}

export { RuleCategory };

export type EntityType = "Medication" | "Condition" | "Procedure" | "Other";

export function rotateBlock90(bb: BlockBoundingBox): BlockBoundingBox {
  return {
    top: bb.left,
    left: 1 - (bb.top + bb.height),
    width: bb.height,
    height: bb.width,
  };
}

export function rotateBlock180(bb: BlockBoundingBox): BlockBoundingBox {
  return {
    top: 1 - bb.top,
    left: 1 - bb.left,
    width: bb.width,
    height: bb.height,
  };
}

export function rotateBlock270(bb: BlockBoundingBox): BlockBoundingBox {
  return {
    top: 1 - bb.left,
    left: bb.top,
    width: bb.height,
    height: bb.width,
  };
}

export function resizeBoundingBoxSurroundIntersectingBlocks(boundingBox: BlockBoundingBox, blocks: Block[]) {
  // given a bounding box, find all blocks that intersect with it and resize the bounding box to surround them if needed
  const intersectingBlocks = blocks.filter((block) => {
    return (
      block.boundingBox.left < boundingBox.left + boundingBox.width &&
      block.boundingBox.left + block.boundingBox.width > boundingBox.left &&
      block.boundingBox.top < boundingBox.top + boundingBox.height &&
      block.boundingBox.top + block.boundingBox.height > boundingBox.top
    );
  });
  const firstBlock = intersectingBlocks[0];
  if (firstBlock !== undefined) {
    // lets just start from the first intersecting block and expand the bounding box to surround all intersecting blocks
    let left = firstBlock.boundingBox.left;
    let top = firstBlock.boundingBox.top;
    let right = firstBlock.boundingBox.left + firstBlock.boundingBox.width;
    let bottom = firstBlock.boundingBox.top + firstBlock.boundingBox.height;
    // we already checked the first intersecting block so we can skip it
    intersectingBlocks.slice(1).forEach((block) => {
      left = Math.min(left, block.boundingBox.left);
      top = Math.min(top, block.boundingBox.top);
      right = Math.max(right, block.boundingBox.left + block.boundingBox.width);
      bottom = Math.max(bottom, block.boundingBox.top + block.boundingBox.height);
    });
    boundingBox.left = left;
    boundingBox.top = top;
    boundingBox.width = right - left;
    boundingBox.height = bottom - top;
  }

  return boundingBox;
}

export function ianaTimezoneId() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function isFullDate(blockDate: BlockDate): boolean {
  return blockDate.monthDay?.day !== undefined;
}

export function parseDate(date: Maybe<string> | undefined): Date | undefined {
  return date ? parseISO(date) : undefined;
}

export function formatRelativeTime(timestamp?: string): string {
  if (timestamp === undefined) return "";
  return formatRelative(parseISO(timestamp), new Date());
}

export function diffInDays(start?: string, end?: string): number | undefined {
  if (start === undefined || end === undefined) return undefined;
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  return differenceInDays(startDate, endDate);
}

/*
Formats the date in the format the backend uses, yyyy-mm-dd
 */
export function formatDateBackend(date: Date | undefined | null): string | undefined {
  return date && !isNaN(date.getTime()) ? format(date, "yyyy-MM-dd") : undefined;
}

export function formatDateBackendGuaranteed(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

export function displayDate(date?: Date): string {
  if (!date || isNaN(date.getTime())) return "";
  return format(date, "MM/dd/yyyy");
}

const PREFERRED_TIME_OF_DAY_FORMAT = "h:mmaaa";

export function displayDateAndTime(date?: Date): string {
  if (!date || !isValid(date)) return "";
  return format(date, `MM/dd/yyyy ${PREFERRED_TIME_OF_DAY_FORMAT}`);
}

// If same day, only shows time
export function displayDateAndTimeCompact(dateString: string | undefined | null) {
  if (!dateString) return "";
  const date = parseISO(dateString);
  const sameDay = isSameDay(date, Date.now());
  if (sameDay) {
    return format(date, PREFERRED_TIME_OF_DAY_FORMAT);
  } else {
    return displayDateAndTime(date);
  }
}

export function displayDateAndTimeString(dateString: string | undefined | null) {
  if (!dateString) return "";
  return displayDateAndTime(parseISO(dateString));
}

export function displayDateString(dateString: string | undefined | null): string {
  if (!dateString) return "";
  return displayDate(parseISO(dateString));
}

export function displayTimeOfDay(dateString: string | undefined | null): string {
  if (!dateString) return "";
  return format(parseISO(dateString), PREFERRED_TIME_OF_DAY_FORMAT);
}

export function displayMillisecondsAsSeconds(ms: number): string {
  return (ms / 1000).toFixed(1) + "s";
}

export function displayDateLoose(dateString: string | undefined | null): string {
  if (!dateString) return "";
  const date = parseISO(dateString);
  if (date.getUTCMonth() === 0) {
    return format(date, "yyyy");
  } else {
    return format(date, "MMM yyyy");
  }
}

export function displayISODateLoose(date: Date | undefined | null): string {
  if (!date || !isValid(date)) return "";

  return format(date, "MMM yyyy");
}

export function displayMimeType(contentType: string) {
  switch (contentType) {
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "Excel";
    case "application/vnd.ms-excel":
      return "Excel";
    case "text/csv":
      return "CSV";
    case "application/pdf":
      return "PDF";
    case "image/jpeg":
      return "JPEG";
    case "image/png":
      return "PNG";
    case "application/zip":
      return "ZIP";
    default:
      return contentType;
  }
}

export function parseSafeInt(value: string | undefined | null): number | undefined {
  const parsed = value ? parseInt(value) : undefined;
  return parsed !== undefined && !isNaN(parsed) ? parsed : undefined;
}

export function parseSafeJSON<T>(value: string | undefined | null): T | undefined {
  if (value === null || value === undefined) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

export const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
export const isValidEmail = (email: string) => emailRegex.test(email);

export { PageType };
export const PAGE_TYPES = Object.values(PageType);

export const DISTINCT_MODES = Object.values(DistinctMode);

export const CELL_TYPES = Object.values(CellType);

export { CellKey };
export const CELL_KEYS = Object.values(CellKey);

export function displayPageType(pt: PageType): string {
  switch (pt) {
    case PageType.RecordCertification:
      return "Record Certification";
    case PageType.OperativeReport:
      return "Operative Report";
    case PageType.PathologyReport:
      return "Pathology Report";
    case PageType.Pharmacy:
      return "Pharmacy";
    case PageType.PatientEducation:
      return "Patient Education";
    case PageType.DeathCertificate:
      return "Death Certificate";
    case PageType.Cover:
      return "Cover Page";
    case PageType.ProgressNote:
      return "Progress Note";
    case PageType.Ecg:
      return "ECG";
    case PageType.CytometryReport:
      return "Cytometry";
    case PageType.Authorization:
      return "Authorization";
    case PageType.Questionnaire:
      return "Questionnaire";
    case PageType.FactSheet:
      return "Fact Sheet";
    case PageType.LabReport:
      return "Lab Report";
    case PageType.RadiologyReport:
      return "Radiology Report";
    default:
      return "";
  }
}

export interface MatchingBlock {
  block: Block;
  entity?: EntityReference;
  offsetStart: number;
  offsetEnd: number;
  preSurroundings: BlockSurroundings[];
  postSurroundings: BlockSurroundings[];
  entities: BlockEntity[];
  pageType?: PageType;
  clusterType?: ClusterType;
}

export interface Dosage {
  units: string;
  value?: number;
}

export function displayDosage(dosage: Dosage | undefined): string {
  if (dosage) {
    if (dosage.value) {
      return `${dosage.value} ${dosage.units}`;
    } else {
      return dosage.units;
    }
  } else return "";
}

export { CancerStage };

export function displayCancerStage(cs: CancerStage): string {
  switch (cs) {
    case "unstaged":
      return "Unstaged";
    case "zero":
      return "0";
    default:
      return cs;
  }
}

export interface BlockEntity {
  entity: EntityReference;
  offsetStart: number;
  offsetEnd: number;
  isNegated: boolean;
  dosage?: Dosage;
  cancerStage?: CancerStage;
  cancer?: string;
  cancerType?: string;
  cancerSubtype?: string;
  cancerRecurrence?: boolean;
  medQuantity?: number;
  medTimesPerDay?: number;
  medDaySupply?: number;
  ndcCode?: string;
  alias?: string;
  adjustedScore?: number;
}

export interface BlockSurroundings {
  block: { index: number; text: string };
  entities: Array<{ offsetStart: number; offsetEnd: number }>;
}

export const emptyLensHitFilters: LensHitsFilterParamsInput = {
  rules: [],
  pageTypes: [],
  notPageTypes: undefined,
  clusterTypes: [],
  notClusterTypes: undefined,
  pageLabels: [],
  notPageLabels: undefined,
  includeNegated: undefined,
  dateStart: undefined,
  dateEnd: undefined,
  documentIds: [],
  intakeIds: [],
  cancerStages: [],
  cancerRecurrence: undefined,
  filterIdentity: undefined,
  pageIdentityGroupId: undefined,
  lensHitIds: undefined,
  pageNumbers: undefined,
  ruleProfileFieldId: undefined,
  ruleIds: [],
  recommendedOnly: false,
  documentRequiredProperties: [],
  documentExcludedProperties: [],
};

export function makeLensHitFilters(partial: Partial<LensHitsFilterParamsInput>): LensHitsFilterParamsInput {
  return { ...emptyLensHitFilters, ...partial };
}

export interface CollapsedHit {
  blockDocumentId: number;
  blockIndex: number;
  blockIntakeId: number;
  blockIntakePage: number;
  blockPage: number;
  hitId: number;
}

export interface LensHit {
  block: Block;
  hitId: number;
  lensRuleId: number;
  hitOffsetStart: number;
  hitOffsetEnd: number;
  preSurroundings: BlockSurroundings[];
  postSurroundings: BlockSurroundings[];
  ruleSubtype: string;
  ruleDescription: string;
  ruleType: "ContainsText" | "IncludesEntity";
  ruleEntityId?: string;
  pageType?: PageType;
  pageLabels?: string[];
  entities: BlockEntity[];
  blockInForm: boolean;
  formFieldId?: number;
  clusterType?: ClusterType;
  collapsedHits: CollapsedHit[];
  isRecommended: boolean;
}

export function itemIsLensHit(item: LensHit | MatchingBlock): item is LensHit {
  return (item as LensHit).hitId !== undefined;
}

export function itemIsEntityBlock(item: LensHit | MatchingBlock): item is MatchingBlock {
  return (item as LensHit).hitId === undefined;
}

interface ProcessedCardItem {
  hit: LensHit | undefined;
  matchingBlock: MatchingBlock | undefined;
  entityOfItem: BlockEntity | undefined;
  block: Block;
  date: BlockDate | undefined | null;
  blockDate: BlockDateModel | undefined;
  isTextHit: boolean | undefined;
  hitInForm: boolean | undefined;
  hitOffsets:
    | {
        start: number;
        end: number;
      }
    | undefined;
  sectionText: string;
  entitiesNoDupes: BlockEntity[];
}

export function entityOfItem(item: LensHit | MatchingBlock): BlockEntity | undefined {
  const hit: LensHit | undefined = itemIsLensHit(item) ? item : undefined;
  const matchingBlock: MatchingBlock | undefined = itemIsEntityBlock(item) ? item : undefined;

  const entityId = hit?.ruleEntityId || matchingBlock?.entity?.id;
  return entityId ? item.entities.find((ent) => ent.entity.id === entityId) : undefined;
}

export function processCardItem(item: LensHit | MatchingBlock): ProcessedCardItem {
  const hit: LensHit | undefined = itemIsLensHit(item) ? item : undefined;
  const matchingBlock: MatchingBlock | undefined = itemIsEntityBlock(item) ? item : undefined;

  const isTextHit = hit && hit.ruleType === "ContainsText";

  const block = hit?.block || matchingBlock!.block;
  const date = block.date;
  const blockDate = date ? new BlockDateModel(date) : undefined;

  const entityId = hit?.ruleEntityId || matchingBlock?.entity?.id;
  const hitOffsets =
    hit && hit.hitOffsetStart !== -1 && hit.hitOffsetEnd !== -1
      ? { start: hit.hitOffsetStart, end: hit.hitOffsetEnd }
      : matchingBlock
        ? { start: matchingBlock.offsetStart, end: matchingBlock.offsetEnd }
        : undefined;

  const clusterType = item.clusterType;
  const pageType = item.pageType;
  const sectionText = clusterType
    ? displayClusterType(clusterType) + (pageType ? ", " + displayPageType(pageType) : "")
    : pageType
      ? displayPageType(pageType)
      : "";

  return {
    hit,
    matchingBlock,
    entityOfItem: entityOfItem(item),
    block,
    date,
    blockDate,
    isTextHit,
    hitInForm: hit?.blockInForm,
    hitOffsets,
    sectionText,
    entitiesNoDupes: item.entities
      ? item.entities.filter(
          (i) =>
            // Not the main entity
            i.entity.id !== entityId &&
            // And no shared offsets with the main hit (happens for dupes)
            !(i.offsetStart === hitOffsets?.start || i.offsetEnd === hitOffsets?.end)
        )
      : [],
  };
}

export interface GetLensRunResponse {
  hits: LensHit[];
  fullCountPreIdentity: number;
  fullCount: number;
}

export interface DataExportResponse {
  pdfBlob: ArrayBuffer;
  fileName: string;
}

export interface LensProfileRule {
  description: string;
  categoryId: string;
  totalHits: number;
  hitYears: number[];
}

export interface LensProfileElement {
  group: string;
  rules: LensProfileRule[];
}

export interface GetCaseEntityBlocksResponse {
  blocks: MatchingBlock[];
  fullCount: number;
}

export interface GetCaseMatchingBlocksResponse {
  blocks: MatchingBlock[];
  fullCount: number;
}

export interface GetCaseBadBlocksResponse {
  blockGroups: Block[][];
  fullCount: number;
}

export { CaseReviewStatus };
export const CaseReviewStatusByValue = Object.fromEntries(
  Object.values(CaseReviewStatus).map((value) => {
    return [value as string, value as CaseReviewStatus];
  })
);

export function displayReviewStatus(status: CaseReviewStatus): string {
  switch (status) {
    case CaseReviewStatus.New:
      return "Pending";
    case CaseReviewStatus.Complete:
      return "Complete";
    case CaseReviewStatus.NewDocuments:
      return "Pending - New Documents";
    case CaseReviewStatus.NoDocuments:
      return "No Documents";
  }
}

export function displayDocumentStatus(status: DocumentStatus): string {
  switch (status) {
    case DocumentStatus.PendingReview:
      return "Pending Review";
    case DocumentStatus.Processing:
      return "Processing";
    case DocumentStatus.Error:
      return "Error";
    case DocumentStatus.Reviewed:
      return "Complete";
  }
}

export { CaseClientStatus };
export const CaseClientStatusByValue = Object.fromEntries(
  Object.values(CaseClientStatus).map((value) => {
    return [value as string, value as CaseClientStatus];
  })
);

export function displayCaseClientStatus(status: CaseClientStatus): string {
  switch (status) {
    case CaseClientStatus.Active:
      return "Active";
    case CaseClientStatus.Inactive:
      return "Inactive";
  }
}

export type BlockIgnoreReason = "Duplicate" | "Bad Match" | "Other";

export interface PageTypeCount {
  pageType: PageType;
  count: number;
}

export interface ClusterTypeCount {
  clusterType: ClusterType;
  count: number;
}

export interface PageLabelCount {
  labelName: string;
  count: number;
}

export interface CancerStageCount {
  cancerStage: CancerStage;
  count: number;
}

export interface LensRuleType {
  category: RuleCategory;
  subtype: string;
  description: string;
}

export function encodeLensRuleType(rt: LensRuleType): string {
  const s = JSON.stringify(rt);
  return Base64.encode(s);
}

export function decodeLensRuleType(encoded: string): LensRuleType | undefined {
  try {
    const json = Base64.decode(encoded);
    const res = JSON.parse(json) as LensRuleType;
    if (!res.description || !res.category || !res.subtype) {
      return undefined;
    } else return res;
  } catch (e) {
    return undefined;
  }
}

export type { CategoryFindingSummary };

export interface GetCaseResponse {
  case: Case;
}

export function documentStatusText(status: DocumentStatus): string {
  switch (status) {
    case "processing":
      return "Processing";
    case "pending_review":
      return "Pending Review";
    case "reviewed":
      return "Complete";
    case "error":
      return "Error";
    default:
      return "Error";
  }
}

export function caseWildcardPubnubChannel(caseId: number): string {
  return `case_${caseId}.*`;
}

export function lensWildcardPubnubChannel(lensId: number): string {
  return `lens_${lensId}_profile_update`;
}

export interface Document {
  id: number;
  name: string;
  processedOn?: string;
  uploadedAt: string;
  sizeBytes: number;
  pages: number;
  contentType: string;
  averageConfidence?: number;
  intakeId?: number;
  partNumber?: number;
  startPage?: number;
  status: DocumentStatus;
  // processingDetails is not part of the Backend model
  // we use it track updates from PubNub
  processingDetails?: ProcessingDetails;
}

export interface DocumentIntakeView {
  id: number;
  caseId: number;
  name: string;
  sizeBytes: number;
  contentType: string;
  pageCount: number;
  createdAt: string;
  failed: boolean;
}

export interface GetCaseDocumentsResponse {
  documents: Document[];
  intakes: DocumentIntakeView[];
}

export interface ConceptView {
  id: string;
  name: string;
  description?: string;
}

export interface EntityReference {
  id: string;
  name: string;
  type: EntityType;
}

export interface EntityReferenceCount {
  entity: EntityReference;
  count: number;
}

export type QueryType = "ContainsText" | "IncludesEntity";

export interface ContainsText {
  text: string;
  phraseSlop?: number;
  fuzziness?: string;
}

export interface IncludesEntity {
  entityId: string;
  entityType: EntityType;
}

export interface LensRule {
  ruleId: number;
  category: RuleCategory;
  subtype: string;
  description: string;
  fieldId?: number;
  note?: string;
  textQuery?: ContainsText;
  entityQuery?: IncludesEntity;
  sectionTypes: Array<ClusterType | PageType>;
}

export interface GetLensResponse {
  lens: Lens;
  rules: LensRule[];
}

export interface ProblemDocumentSummary {
  documentId?: number;
  intakeId?: number;
  caseId?: number;
  issue: string;
  reason?: string;
  name?: string;
  sizeBytes?: number;
  contentType?: string;
  pageCount?: number;
  intakeFailureCause?: string;
  transferClientCaseId?: string;
  transferFilename?: string;
  transferContentType?: string;
  transferContentLength?: number;
  transferLastModified?: string;
}

export interface GetProblemDocumentsResponse {
  transfer: ProblemDocumentSummary[];
  intake: ProblemDocumentSummary[];
  textract: ProblemDocumentSummary[];
  wits: ProblemDocumentSummary[];
  index: ProblemDocumentSummary[];
  lens: ProblemDocumentSummary[];
  witsPages: ProblemDocumentSummary[];
  missingPages: ProblemDocumentSummary[];
}

export interface WitsUmlsEntity {
  concept_id: string;
  name: string;
  description?: string;
  score?: number;
  umls_entity_type: string;
}

export interface DetectEntitiesResponse {
  umlsEntities?: BlockEntity[];
  dateEntities?: {
    offset_start: number;
    offset_end: number;
    date: {
      date_text: string;
    };
  }[];
  grams?: string[];
}

export const UMLS_ENTITY_TYPE = [
  "condition",
  "medication",
  "procedure",
  "device",
  "dosage",
  "other",
  "nonmedical",
] as const;
export type UmlsEntityType = (typeof UMLS_ENTITY_TYPE)[number];

export function umlsToEntityType(umls: UmlsEntityType): EntityType | undefined {
  switch (umls) {
    case "condition":
      return "Condition";
    case "medication":
      return "Medication";
    case "procedure":
      return "Procedure";
    case "other":
      return "Other";
    default:
      return undefined;
  }
}

export interface WitsConcept {
  id: string;
  canonicalName: string;
  definition?: string;
  aliases: string[];
  umlsEntityType: UmlsEntityType;
  types: string[];
  codes: {
    source: string;
    code: string;
  }[];
  refinedType?: string;
  exclude: boolean;
  penalty: number;
  requiredFragments: string[];
  customProperties: Record<string, string>;
}

export interface InfraStatsBlock {
  queueSize: number;
  inflightCount: number;
}

export interface SQSStats {
  processBackgroundCount: InfraStatsBlock;
  processCount: InfraStatsBlock;
  processPriorityCount: InfraStatsBlock;
  scheduledEvent: InfraStatsBlock;
  scheduledEventPriorityCount: InfraStatsBlock;
}

export interface DetailedStatsHistoryItem {
  timestamp: number;
  data: GetDetailedInfraStatsResponse;
}

export interface GetDetailedInfraStatsResponse {
  sqsStats: SQSStats;
  unpartitionedStats: Array<UnpartitionedStat>;
  clientIdStats: Array<ClientIdStat>;
  jobTypeStats: Array<JobTypeStat>;
}

export interface UnpartitionedStat {
  name: string;
  stats: InfraStatsBlock;
}

export interface ClientIdStat {
  clientId: number;
  stats: InfraStatsBlock;
}

export interface JobTypeStat {
  jobType: string;
  stats: InfraStatsBlock;
}

export interface DocumentData {
  contentType: string;
  data: ArrayBuffer;
}

export interface CheckCaseResult {
  line: number;
  error?: string;
  caseInfo?: CaseInfo;
  assigneeEmail?: string;
  rawRecord?: Record<string, unknown>;
}

export interface CheckCasesResponse {
  results: CheckCaseResult[];
}

export interface UploadCaseInvoiceEntriesResponse {
  failures: {
    message: string;
    lineNumber: number;
    rawRecord: any;
  }[];
  successes: {
    lineNumber: number;
    row: {
      caseId: number;
      message: string;
    };
  }[];
}

export interface FormFieldValuesUI extends Omit<FFV, "valueGroups"> {
  valueGroups: FormFieldValueGroupUI[];
}

export function sanitizeInputString(input: string): string {
  // replace em and en dashes with hyphens
  const hyphenated = input.replaceAll(/\u2014|\u2013/g, "-");
  // replace left and right double quotes with neutral quote
  const quoted = hyphenated.replaceAll(/\u201c|\u201d/g, `"`);
  // replace left and right single quote with apostrophes
  const apostrophed = quoted.replaceAll(/\u2018|\u2019/g, `'`);
  return apostrophed.trim();
}

export type CellValueUnion = string | boolean | number | Date | string[];

export function isNoneCell(value: CellValue): boolean {
  if (!value?.type) {
    return true;
  }
  switch (value.type) {
    case CellType.Text:
      return !value.textValue;
    case CellType.Select:
      return !value.textValue;
    case CellType.Boolean:
      return value.booleanValue === null || value.booleanValue === undefined;
    case CellType.Date:
      return !value.dateValue;
    case CellType.Float:
      return value.floatValue === undefined || Number.isNaN(value.floatValue);
    case CellType.Integer:
      return !Number.isInteger(value.integerValue);
    case CellType.MultiSelect:
      return !value.multiSelectValue;
  }
}

export function isNoneCellOrNullish(value: CellValue | undefined | null): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  return isNoneCell(value);
}

export function gqlCellValueFromValue(type: CellType, value?: CellValueUnion): CellValue {
  if (value === undefined) {
    return { type };
  }
  switch (type) {
    case CellType.Text: {
      return {
        type,
        textValue: value as string,
      };
    }
    case CellType.Select: {
      return {
        type: CellType.Text,
        textValue: value as string,
      };
    }
    case CellType.Boolean: {
      return { type, booleanValue: value as boolean };
    }
    case CellType.Date: {
      if (typeof value === "string") {
        return { type, dateValue: value as string };
      } else {
        const d = value as Date;
        if (d && isNaN(d.getTime())) {
          // A very special case here occurs when the date cannot be formatted
          // because it is invalid; we want to preserve it so that the date picker
          // can maintain its local state, so we shove the `Date` into the CellValue
          // as a string. The only consumer of this bad date value will be the
          // DatePicker itself, and it is expecting this value
          return { type, dateValue: d as unknown as string };
        } else {
          return { type, dateValue: formatDateBackend(value as Date) };
        }
      }
    }
    case CellType.Float: {
      return { type, floatValue: value as number };
    }
    case CellType.Integer: {
      return { type, integerValue: value as number };
    }
    case CellType.MultiSelect: {
      return { type, multiSelectValue: value as string[] };
    }
  }
}

export function cellValueFromGQL(value: CellValue | undefined | null): CellValueUnion | undefined {
  if (!value) return undefined;
  if (!value?.type) {
    return undefined;
  }
  switch (value.type) {
    case CellType.Text:
      return value.textValue !== null ? value.textValue : undefined;
    case CellType.Select:
      return value.textValue !== null ? value.textValue : undefined;
    case CellType.Boolean:
      return value.booleanValue !== null ? value.booleanValue : undefined;
    case CellType.Date:
      if (typeof value.dateValue === "string") {
        return value.dateValue ? parseISO(value.dateValue) : undefined;
      } else {
        // This is the inverse of the special case above
        // We have a 'Date', not a string, which means this value
        // did not come from the API, but is a locally edited, currently invalid,
        // `Date` instance. We want to simply forward it along so the user can
        // continue typing until they have typed a valid date.
        return value.dateValue as unknown as Date;
      }
    case CellType.Float:
      return value.floatValue !== null ? value.floatValue : undefined;
    case CellType.Integer:
      return value.integerValue !== null ? value.integerValue : undefined;
    case CellType.MultiSelect:
      return value.multiSelectValue !== null ? value.multiSelectValue : undefined;
  }
}

export function numberCellValueFromGQL(value: CellValue | undefined | null): number | undefined {
  if (!value) return undefined;
  if (!value?.type || (value.type !== CellType.Integer && value.type !== CellType.Float)) {
    return undefined;
  }
  if (value.type === CellType.Integer) {
    return stripNull(value.integerValue);
  } else {
    return stripNull(value.floatValue);
  }
}

export interface ValueGroupIntakeReferenceForInsert {
  intakeId: number;
  intakePage: number;
  blockIndex?: number | null;
  pageRangeStart?: number;
  pageRangeEnd?: number;
}

export interface ValueGroupReferenceForInsert {
  documentId: number;
  blockPage: number;
  blockIndex?: number;
  pageRangeStart?: number;
  pageRangeEnd?: number;
}

export interface FieldCellValueUI extends FieldCellValue {
  error?: string;
}

export const UNGROUPED_CELL_ID = -1;
export const INTERNAL_USER_NAME = "Pattern Data";

export enum RowViewState {
  ReadCollapsed = "read_collapsed",
  ReadExpanded = "read_expanded",
  EditCollapsed = "edit_collapsed",
  EditExpanded = "edit_expanded",
}

export function returnToViewState(viewState: RowViewState): RowViewState {
  return viewState === RowViewState.EditCollapsed ? RowViewState.ReadCollapsed : RowViewState.ReadExpanded;
}

export function calculateDefaultRowViewState(valueGroupCount: number): RowViewState {
  return valueGroupCount > 3 ? RowViewState.ReadCollapsed : RowViewState.ReadExpanded;
}

export function isEditViewState(viewState: RowViewState): boolean {
  return viewState === RowViewState.EditCollapsed || viewState === RowViewState.EditExpanded;
}

export interface FormFieldValueGroupUI extends Omit<FFVG, "values" | "groupIndex"> {
  isNew?: boolean;
  touched?: boolean;
  values: Array<FieldCellValueUI>;
  id: number;
  viewState: RowViewState;
  responseNumber: number;
  availableTags: TagView[];
}

export interface FormFieldValuesUI extends Omit<FormFieldValues, "valueGroups"> {
  valueGroups: Array<FormFieldValueGroupUI>;
}

export interface FieldValueGroupReference {
  id: number;
  valueGroupId: number;
  documentId: number;
  documentIntakeId: number;
  documentName: string;
  blockIndex?: number;
  blockPage: number;
  originalPage: number;
  pageRangeStart?: number;
  pageRangeEnd?: number;
}

export function formatFileNameForUpload(fileName: string): string {
  return fileName.replace(/[—–]/g, "-").replace(/\s+/g, " ");
}

export type EditableFormField = Omit<FormField, "id">;

export type { FormSection };

export interface FormImportErrorRow {
  rowNumber: number;
  clientCaseId?: string;
  errors: string[];
}

export interface ImportFieldValuesResponse {
  errors: FormImportErrorRow[];
  records: {
    clientCaseId: string;
    cellValues: { value?: string | number | boolean }[];
    notes?: string;
    valueGroupIndex?: number;
    delete: boolean;
  }[];
  errorTotal: number;
  recordTotal: number;
  recordCases: number;
  checkOnly: boolean;
}

interface ImportSectionValuesResponseRecord {
  clientCaseId: string;
  sectionId: number;
  reviewState: SectionReviewState;
  outcomeStatus: OutcomeStatus;
  outcomeComment?: string;
  outcomeAssigneeId?: number;
  validationStatus: ValidationStatus;
  validationComment?: string;
  validationAssigneeId?: number;
}

export interface ImportSectionValuesResponse {
  errors: FormImportErrorRow[];
  records: ImportSectionValuesResponseRecord[];
  errorTotal: number;
  recordTotal: number;
  recordCases: number;
  checkOnly: boolean;
}

export interface ImportDocumentPropertyError {
  rowNumber: number;
  clientCaseId: string;
  intakeId: number;
  propertyKey: string;
  errors: string[];
}

export interface ImportDocumentPropertyRecord {
  clientCaseId: string;
  intakeId: number;
  propertyKey: string;
  propertyValue: string;
}

export interface ImportDocumentPropertiesResponse {
  records?: ImportDocumentPropertyRecord[];
  errors?: ImportDocumentPropertyError[];
  checkOnly: boolean;
}

class HttpBackend {
  constructor(private base: string) {}

  private callConfig = { withCredentials: true };

  async doPost<A, B>(
    path: string,
    body?: A,
    callConfig: AxiosRequestConfig = this.callConfig,
    noRedirect = false
  ): Promise<BackendResponse<B>> {
    try {
      const response = await axios.post<B>(this.base + path, body, callConfig);
      if (response.status === 401 && !noRedirect) {
        redirectToSignin();
      }
      return this.handleResponse(response);
    } catch (e: any) {
      return this.handleResponse(e.response as AxiosResponse);
    }
  }

  async makePost<A, B>(
    path: string,
    body?: A,
    callConfig: AxiosRequestConfig = this.callConfig,
    noRedirect = false
  ): Promise<AxiosResponse<B>> {
    try {
      const response = await axios.post<B>(this.base + path, body, callConfig);
      if (response.status === 401 && !noRedirect) {
        redirectToSignin();
      }
      return response;
    } catch (e: any | AxiosError) {
      if (e.isAxiosError) {
        if (e.response?.status === 401 && !noRedirect) {
          redirectToSignin();
        }
      }
      throw e;
    }
  }

  async post<A, B>(
    path: string,
    body?: A,
    callConfig: AxiosRequestConfig = this.callConfig,
    noRedirect = false
  ): Promise<B> {
    return this.makePost<A, B>(path, body, callConfig, noRedirect).then((r) => r.data);
  }

  mapSuccess<T, A>(r: BackendResponse<T>, fn: (t: T) => A, noRedirect?: boolean): A | undefined {
    if (r.tag === "success") {
      return fn(r.value);
    } else {
      if (r.tag === "unauthorized" && !noRedirect) {
        redirectToSignin();
      }
      return undefined;
    }
  }

  async mapSuccessAsync<T, A>(
    r: Promise<BackendResponse<T>>,
    fn: (t: T) => A,
    noRedirect?: boolean
  ): Promise<A | undefined> {
    const result = await r;
    return this.mapSuccess(result, fn, noRedirect);
  }

  handleResponse<T>(response: AxiosResponse<T>): BackendResponse<T> {
    if (!response) {
      return serverError;
    }
    const error = (response.data as any).message as string | undefined;
    if (response.status === 401) {
      return unauthorized;
    } else if (response.status === 404) {
      return { tag: "not_found", error };
    } else if (response.status === 403) {
      return forbidden;
    } else if (response.status >= 500) {
      return serverError;
    } else if (response.status >= 200 && response.status < 300) {
      return success(response.data);
    } else {
      // console.log(response);
      return { tag: "error", error };
    }
  }

  async healthCheck() {
    return this.post("/management/healthcheck");
  }

  async login(email: string, password: string, ianaTimezone: string): Promise<BackendResponse<UserInfo>> {
    return this.doPost(
      "/auth/login",
      {
        email: email.toLowerCase(),
        password,
        ianaTimezoneId: ianaTimezone,
      },
      this.callConfig,
      true
    );
  }

  async loginWithClientId(email: string, password: string, clientId: number, ianaTimezone: string): Promise<void> {
    return this.post(
      "/auth/loginPerson",
      {
        email: email.toLowerCase(),
        password,
        clientId,
        ianaTimezoneId: ianaTimezone,
      },
      this.callConfig,
      true
    );
  }

  async listUsersForCredentials(email: string, password: string): Promise<UserForCredentialResponse> {
    return this.post(
      "/auth/listUsersForCredentials",
      {
        email: email.toLowerCase(),
        password: password,
      },
      this.callConfig,
      true
    );
  }

  async logout(): Promise<BackendResponse<void>> {
    return this.doPost("/auth/logout");
  }

  async forgotPassword(email: string): Promise<BackendResponse<void>> {
    return this.doPost("/user/forgotPassword", { email });
  }

  async forgotPersonPassword(email: string): Promise<BackendResponse<void>> {
    return this.doPost("/user/forgotPersonPassword", { email });
  }

  async changePassword(token: string, password: string): Promise<BackendResponse<void>> {
    return this.doPost("/user/changeForgottenPassword", {
      token,
      password,
    });
  }

  async changePersonPassword(token: string, password: string): Promise<BackendResponse<{ collectUrl: string }>> {
    return this.doPost("/person/changePassword", {
      token,
      password,
    });
  }

  async getUserApiCredentials(userId: number): Promise<GetUserApiCredentialsResponse> {
    return this.post("/user/getUserApiCredentials", {
      userId,
    });
  }

  async createUserApiCredential(userId: number): Promise<CreateUserApiCredentialResponse> {
    return this.post("/user/createUserApiCredential", {
      userId,
    });
  }

  async revokeUserApiCredential(id: number): Promise<void> {
    return this.post("/user/revokeUserApiCredential", {
      id,
    });
  }

  async emailSupport(subject: string, body: string): Promise<BackendResponse<void>> {
    return this.doPost("/client/emailSupport", {
      subject,
      body,
    });
  }

  async inviteUser(
    email: string,
    admin: boolean,
    lenses: number[],
    firstName?: string,
    lastName?: string
  ): Promise<void> {
    return this.post("/user/inviteUser", {
      email: email.toLowerCase(),
      admin: true,
      userType: admin ? "admin" : "reviewer",
      lenses,
      firstName,
      lastName,
    });
  }

  async resendInvite(email: string): Promise<void> {
    return this.post("/user/resendInvite", { email });
  }

  async onboardUser(
    token: string,
    firstName: string,
    lastName: string,
    password?: string
  ): Promise<BackendResponse<UserInfo>> {
    return this.doPost("/user/onboardUser", {
      token,
      firstName,
      lastName,
      password,
    });
  }

  async checkOnboarded(token: string): Promise<BackendResponse<CheckOnboardedResponse>> {
    return this.doPost("/user/checkOnboarded", { token });
  }

  async createLens(newLens: NewLens): Promise<GetLensResponse> {
    return this.post("/lens/createLens", newLens);
  }

  async deleteCaseExport(caseExportId: number): Promise<{ caseExportId: number }> {
    return this.post<{ caseExportId: number }, void>("/case/deleteCaseExport", { caseExportId }).then(() => ({
      caseExportId,
    }));
  }

  async triggerCaseCoversheetExport(body: {
    caseId: number;
    coversheetDate: string;
    intakeIds: number[];
    merged: boolean;
    bulkZip: boolean;
  }): Promise<{ exports: CaseExport[] }> {
    return this.post("/case/triggerCaseCoversheetExport", body);
  }

  async downloadProfileTemplate(fieldId: number): Promise<{
    fileName: string;
    data: BinaryData;
  }> {
    return this.makePost<{ fieldId: number }, BinaryData>(
      "/case/downloadProfileImportTemplate",
      { fieldId },
      {
        responseType: "blob",
        ...this.callConfig,
      }
    ).then((res) => ({
      fileName:
        parseContentDispositionFilename(res.headers["content-disposition"]) || `${fieldId}-profile-template.xlsx`,
      data: res.data,
    }));
  }

  async downloadCaseTemplate(
    propertyKeys: string[],
    lensId?: number
  ): Promise<{
    fileName: string;
    data: BinaryData;
  }> {
    return this.makePost<{ propertyKeys: string[]; lensId?: number }, BinaryData>(
      "/case/downloadCaseImportTemplate",
      { propertyKeys, lensId },
      {
        responseType: "blob",
        ...this.callConfig,
      }
    ).then((res) => ({
      fileName: parseContentDispositionFilename(res.headers["content-disposition"]) || `case-template.xlsx`,
      data: res.data,
    }));
  }

  async downloadCaseInvoiceEntries(invoiceId: number): Promise<{
    fileName: string;
    data: BinaryData;
  }> {
    return this.makePost<{ invoiceId: number }, BinaryData>(
      "/client/getCaseInvoiceEntriesExport",
      { invoiceId },
      {
        responseType: "blob",
        ...this.callConfig,
      }
    ).then((res) => ({
      fileName:
        parseContentDispositionFilename(res.headers["content-disposition"]) || `${invoiceId}-case-invoice-entries.xlsx`,
      data: res.data,
    }));
  }

  async downloadCaseList(body: {
    lensId: number;
    includeProperties: boolean;
    textQuery?: string;
    caseStatus?: CaseClientStatus;
    reviewStatuses?: CaseReviewStatus[];
    assigneeId?: number;
    plaintiffFirmId?: number;
    reviewStatusReason?: string;
    filters: GetCasesParamsInput;
  }): Promise<{ fileName: string; data: BinaryData }> {
    return this.makePost<typeof body, BinaryData>("/lens/downloadCaseList", body, {
      responseType: "blob",
      ...this.callConfig,
    }).then((res) => ({
      fileName: parseContentDispositionFilename(res.headers["content-disposition"]) || `${body.lensId}-case-list.xlsx`,
      data: res.data,
    }));
  }

  async downloadLensScriptRuns(body: {
    lensId: number;
    ids: number[];
  }): Promise<{ fileName: string; data: BinaryData }> {
    return this.makePost<typeof body, BinaryData>("/lens/downloadLensScriptRuns", body, {
      responseType: "blob",
      ...this.callConfig,
    }).then((res) => ({
      fileName:
        parseContentDispositionFilename(res.headers["content-disposition"]) ||
        `script_results_${body.lensId}_failures.csv`,
      data: res.data,
    }));
  }

  async uploadCaseInvoiceEntries(file: File, invoiceId: number): Promise<UploadCaseInvoiceEntriesResponse> {
    return new Promise<UploadCaseInvoiceEntriesResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        resolve(
          this.post("/client/uploadCaseInvoiceEntries", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-Invoice-Id": invoiceId,
            },
          })
        );
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async upsertCasesFromFile(
    file: File,
    checkOnly: boolean,
    litigationName?: string,
    firmId?: string
  ): Promise<UpsertCasesResponse> {
    return new Promise<UpsertCasesResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        try {
          const result = await this.post<typeof fileData, UpsertCasesResponse>("/case/upsertCasesFromFile", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-File-Name": file.name,
              "X-Check-Only": checkOnly.toString(),
              ...(litigationName ? { "X-Litigation-Name": litigationName } : {}),
              ...(firmId ? { "X-Firm-External-Id": firmId } : {}),
            },
          });
          resolve(result);
        } catch (err) {
          // Non-200 response from server
          reject(err);
        }
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async upsertFirmsFromFile(file: File, checkOnly: boolean): Promise<UpsertFirmsResponse> {
    return new Promise<UpsertFirmsResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        try {
          const result = await this.post<typeof fileData, UpsertFirmsResponse>("/firm/upsertFirmsFromFile", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-Check-Only": checkOnly.toString(),
              "X-File-Name": file.name,
            },
          });

          resolve(result);
        } catch (err) {
          // Non-200 response from server
          reject(err);
        }
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async editCaseClaimantInfo(
    caseId: number,
    caseInfo: CaseInfo,
    userAssigneeId: number | undefined,
    customProperties: KeyValueOptionalInput[]
  ): Promise<CaseInfo> {
    return this.post("/case/editClaimantInfo", {
      caseId,
      givenName: { type: "Set", value: caseInfo.claimantGivenName },
      middleName: caseInfo.claimantMiddleName
        ? {
            type: "Set",
            value: caseInfo.claimantMiddleName,
          }
        : { type: "Clear" },
      surname: { type: "Set", value: caseInfo.claimantSurname },
      dateOfBirth: caseInfo.dateOfBirth ? { type: "Set", value: caseInfo.dateOfBirth } : { type: "Clear" },
      dateOfDeath: caseInfo.dateOfDeath ? { type: "Set", value: caseInfo.dateOfDeath } : { type: "Clear" },
      coCounsel: caseInfo.coCounsel ? { type: "Set", value: caseInfo.coCounsel } : { type: "Clear" },
      userAssignee: userAssigneeId ? { type: "Set", value: userAssigneeId } : { type: "Clear" },
      stateOfResidence: caseInfo.stateOfResidence
        ? { type: "Set", value: caseInfo.stateOfResidence }
        : { type: "Clear" },
      customProperties,
    }).then(() => caseInfo);
  }

  async getCaseByClientCaseId(clientCaseId: string): Promise<GetCaseResponse> {
    return this.post("/case/getCaseByClientCaseId", { clientCaseId });
  }

  async addLensHitsToFormField(body: {
    lensId: number;
    caseId: number;
    fieldId: number;
    filters: LensHitsFilterParamsInput;
  }): Promise<BackendResponse<{ count: number }>> {
    return this.doPost("/case/addLensHitsToFormField", body);
  }

  async testRegex(pattern: string, target: string): Promise<{ start: number; end: number; substring: string } | null> {
    return this.post<
      {
        pattern: string;
        targets: string[];
      },
      {
        results: {
          substring: string;
          start: number;
          end: number;
        }[];
      }
    >("/management/testRegex", {
      pattern,
      targets: [target],
    }).then((response) => {
      const result = response.results[0];
      return result || null;
    });
  }

  async getLensRun(body: {
    lensId: number;
    caseId: number;
    offset: number;
    limit: number;
    includeSurroundings: boolean;
    filters: LensHitsFilterParamsInput;
  }): Promise<GetLensRunResponse> {
    return this.post("/case/getLensRun", body);
  }

  async handleExportResponse(
    response: AxiosResponse,
    defaultName: string
  ): Promise<BackendResponse<DataExportResponse>> {
    if (response.status === 200) {
      const fileName = parseContentDispositionFilename(response.headers["content-disposition"]);
      return success({
        pdfBlob: response.data,
        fileName: fileName || defaultName,
      });
    } else {
      return this.handleResponse(response);
    }
  }

  async getCaseMatchingBlocks(
    lensId: number,
    caseId: number,
    query: string,
    intakeIds?: number[],
    dateStart?: string,
    dateEnd?: string,
    offset?: number,
    limit?: number
  ): Promise<BackendResponse<GetCaseMatchingBlocksResponse>> {
    return this.doPost("/case/getMatchingBlocks", {
      lensId,
      caseId,
      query,
      intakeIds,
      dateStart,
      dateEnd,
      offset: offset || 0,
      limit: limit || 10,
    });
  }

  async getCaseBadBlocks(
    caseId: number,
    offset: number,
    limit: number
  ): Promise<BackendResponse<GetCaseBadBlocksResponse>> {
    return this.doPost("/case/getBadBlocks", {
      caseId,
      offset,
      limit,
    });
  }

  async getInfraStats(): Promise<SQSStats> {
    return this.post("/management/getInfraStats");
  }

  async getDetailedInfraStats(): Promise<GetDetailedInfraStatsResponse> {
    return this.post("/management/getDetailedInfraStats");
  }

  getDocumentUrl(documentId: number): string {
    return `${this.base}/document/getRenderableContent/${documentId}`;
  }

  getCoversheetPreviewUrl(caseId: number, coversheetDate: string): string {
    return `${this.base}/case/getCaseCoversheet/${caseId}?coversheetDate=${coversheetDate}`;
  }

  async getTemplatePreview(
    lensId: number,
    caseId: number,
    profileExportConfig: ProfileExportConfig,
    templateFile: File | undefined
  ) {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return new ArrayBuffer(0);
        }
        const fileData = e.target.result;
        if (fileData instanceof ArrayBuffer) {
          const fileDataArrayBytes = new Uint8Array(fileData);

          resolve(this.postTemplatePreview(lensId, caseId, profileExportConfig, fileDataArrayBytes));
        }
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      templateFile
        ? reader.readAsArrayBuffer(templateFile)
        : resolve(this.postTemplatePreview(lensId, caseId, profileExportConfig, new Uint8Array(0)));
    });
  }

  private async postTemplatePreview(
    lensId: number,
    caseId: number,
    profileExportConfig: ProfileExportConfig,
    fileDataArrayBytes: Uint8Array
  ) {
    const res = await this.makePost<
      {
        lensId: number;
        caseId: number;
        profileExportConfig: ProfileExportConfig;
        templateFileBase64: string;
      },
      ArrayBuffer
    >(
      "/lens/buildTemplatedDocumentForCase",
      {
        lensId: lensId,
        caseId: caseId,
        profileExportConfig: profileExportConfig,
        templateFileBase64: Base64.fromUint8Array(new Uint8Array(fileDataArrayBytes), true),
      },
      {
        responseType: "arraybuffer",
        ...this.callConfig,
      }
    );
    return res.data;
  }

  getIntakeDownloadUrl(intakeId: number): string {
    return `${this.base}/document/getIntakeContent/${intakeId}`;
  }

  async markBlocksAsIgnored(
    documentSegmentId: number,
    blockIndexes: number[],
    reason: BlockIgnoreReason
  ): Promise<void> {
    return this.post("/document/markBlocksIgnored", {
      documentId: documentSegmentId,
      blockIndexes,
      reason,
      ignored: true,
    });
  }

  async editDocumentBlocksQA(
    documentId: number,
    isQAd: boolean,
    page?: number,
    indexRangeStart?: number,
    indexRangeEnd?: number
  ): Promise<BackendResponse<void>> {
    return this.doPost("/document/editBlocksQA", {
      documentId,
      qualityAssured: isQAd,
      page,
      indexRangeStart,
      indexRangeEnd,
    });
  }

  async markDocumentPageViewed(documentId: number, page: number): Promise<BackendResponse<void>> {
    return this.doPost("/document/markPageViewed", {
      documentId,
      page,
    });
  }

  async getProblemDocuments(body: GetProblemDocumentsRequest): Promise<BackendResponse<GetProblemDocumentsResponse>> {
    return this.doPost("/management/getProblemDocuments", body);
  }

  async getFullLensArchive(lensId: number): Promise<BackendResponse<DataExportResponse>> {
    try {
      const response = await axios({
        method: "POST",
        url: `${this.base}/lensManagement/getFullLensArchive`,
        data: {
          lensId,
        },
        responseType: "blob",
        ...this.callConfig,
      });
      return this.handleExportResponse(response, "lens_archive.json");
    } catch (e: any) {
      return this.handleResponse(e.response as AxiosResponse);
    }
  }

  async sendLensFamilyDifferencesReport(lensFamily: string) {
    return this.post("/lensManagement/sendLensFamilyDifferencesReport", { lensFamily });
  }

  async makeLensPatch(body: { lensId: number; addRuleIds?: number[] }): Promise<{ patch: string; filename: string }> {
    const response: AxiosResponse<Record<string, unknown>> = await this.makePost("/lens/makePatch", body);
    const filename = parseContentDispositionFilename(response.headers["content-disposition"] || "") || "patch.json";
    return {
      patch: JSON.stringify(response.data),
      filename: filename,
    };
  }

  async applyLensPatch(lensId: number, duplicateMode: LensPatchApplyDuplicateMode, body: string): Promise<void> {
    return this.post("/lens/applyPatch", body, {
      ...this.callConfig,
      headers: {
        "Content-Type": "application/json",
        "X-Lens-Id": lensId,
        "X-Duplicate-Mode": duplicateMode.toString(),
      },
    });
  }

  async reprocessDocuments(
    caseId: number,
    documentIds: number[],
    stage: ReprocessStage,
    priority: boolean,
    rebuildPDF: boolean
  ): Promise<BackendResponse<void>> {
    return this.post("/document/reprocess", { caseId, documentIds, reprocessType: stage, priority, rebuildPDF });
  }

  async setCaseSummary(caseId: number, summary: EditAction<string>): Promise<void> {
    return this.post("/case/setSummary", {
      caseId,
      summary,
    });
  }

  uploadProfileExportTemplate(
    file: File | undefined,
    fileName: string | undefined,
    lensId: number,
    profileExportConfigId: number
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!file) {
        reject("File is null");
        return;
      }
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        resolve(
          this.post("/lens/uploadAndSetProfileExportConfigTemplateFile", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-Lens-Id": lensId,
              "X-File-Name": fileName,
              "X-Profile-Export-Config-Id": profileExportConfigId,
            },
          })
        );
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  importFieldValuesFromExcel(
    file: File,
    formId: number,
    fieldId: number,
    checkOnly: boolean
  ): Promise<ImportFieldValuesResponse> {
    return new Promise<ImportFieldValuesResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        resolve(
          this.post("/form/importFieldValuesFromExcel", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-Form-Id": formId,
              "X-Field-Id": fieldId,
              "X-Check-Only": checkOnly.toString(),
              "X-File-Name": file.name,
            },
          })
        );
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  importSectionValuesFromExcel(file: File, formId: number, checkOnly: boolean): Promise<ImportSectionValuesResponse> {
    return new Promise<ImportSectionValuesResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        resolve(
          this.post("/form/importSectionValuesFromExcel", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-Form-Id": formId,
              "X-Check-Only": checkOnly.toString(),
              "X-File-Name": file.name,
            },
          })
        );
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  importDocumentPropertiesFromExcel(
    file: File,
    lensId: number,
    checkOnly: boolean
  ): Promise<ImportDocumentPropertiesResponse> {
    return new Promise<ImportDocumentPropertiesResponse>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target) {
          reject("FileReader target was null");
          return;
        }
        const fileData = e.target.result;
        resolve(
          this.post("/document/importDocumentPropertiesFromExcel", fileData, {
            ...this.callConfig,
            headers: {
              "Content-Type": file.type,
              "X-Lens-Id": lensId,
              "X-Check-Only": checkOnly.toString(),
              "X-File-Name": file.name,
            },
          })
        );
      };
      reader.onerror = async (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  async getLookerEmbedUrl(url: string, caseId?: number | null): Promise<{ embedUrl: string }> {
    return this.post("/looker/getEmbedUrl", {
      destinationUrl: url,
      params: caseId ? { caseId } : undefined,
    });
  }

  async detectEntities(text: string): Promise<DetectEntitiesResponse> {
    return this.post("/entity/detectEntities", { text });
  }

  async conceptLookup(conceptIds: string[]): Promise<WitsConcept[]> {
    return this.post("/entity/conceptLookup", { conceptIds });
  }

  async conceptChildren(conceptIds: string[]): Promise<{ concepts: WitsConcept[][] }> {
    return this.post("/wits/concept/getChildren", { conceptIds });
  }

  async conceptSearch(nameQuery: string, offset: number, limit: number): Promise<{ concepts?: WitsConcept[] }> {
    return this.post("/wits/concept/searchConcepts", { nameQuery, offset, limit });
  }
}

export type IntakeStatusUpdateEvent = {
  caseId: number;
  documentIntakeId: number;
  status: DocumentStatus;
  timestamp: string;
  segmentMetrics?: SegmentMetrics;
  type: "IntakeStatusUpdate";
};
export type SegmentStatusUpdateEvent = {
  caseId: number;
  documentIntakeId: number;
  processingDetails?: {
    stageDescription: string;
    stageNumber: number;
    stageProgress: number;
    stageTotal: number;
  };
  segmentId: number;
  segmentPartIndex: number;
  status: DocumentStatus;
  timestamp: string;
  type: "SegmentStatusUpdate";
};
export type IntakeSplitCompleteEvent = {
  caseId: number;
  segmentCount: number;
  documentIntakeId: number;
  timestamp: string;
  type: "IntakeSplitComplete";
};

export function isIntakeSplitCompleteEvent(evt: any): evt is IntakeSplitCompleteEvent {
  const ie = evt as IntakeSplitCompleteEvent;
  return ie.type === "IntakeSplitComplete" && ie.segmentCount !== undefined && ie.documentIntakeId !== undefined;
}

export function isIntakeStatusUpdateEvent(evt: any): evt is IntakeStatusUpdateEvent {
  const ie = evt as IntakeStatusUpdateEvent;
  return ie.type === "IntakeStatusUpdate" && ie.documentIntakeId !== undefined && ie.caseId !== undefined;
}

export function isSegmentStatusUpdateEvent(evt: any): evt is SegmentStatusUpdateEvent {
  return evt.type === "SegmentStatusUpdate" && evt.segmentId !== undefined;
}

export type ReprocessStage = "re_extract" | "re_wits" | "re_index";

export type LensPatchApplyDuplicateMode = "Allow" | "IgnoreByQuery" | "IgnoreByQueryAndLocation";

export interface DocumentEventBase {
  type: "StatusUpdate" | "NewDocument";
  caseId: number;
  documentId: number;
  timestamp: string;
}

export interface ProcessingDetails {
  stageDescription: string;
  stageNumber: number;
  stageTotal: number;
  /** Value between 0 and 1 */
  stageProgress: number;
}

export interface UpsertCasesResponse {
  successes: UpsertCaseResult[];
  failures: IntakeError[];
  successTotal?: number;
  successUpdateCount?: number;
  successCreateCount?: number;
  failureTotal?: number;
}

export interface UpsertFirmsResponse {
  successes: UpsertFirmResult[];
  failures: IntakeError[];
}

export interface UpsertCaseResult {
  actions: string[];
  caseId: number;
  clientCaseId: string;
  dateOfBirth: string;
  givenName: string;
  surname: string;
}

export interface UpsertFirmResult {
  clientId: number;
  lensId: number;
  externalId: string;
  name: string;
  actions: string[];
  createdId: number | undefined;
}

export interface IntakeError {
  message: string;
  lineNumber: number;
  rawRecord?: Record<string, string>;
}

export const backend = new HttpBackend(Config.backendBase);

export type { Permission, UserSession };

export const CASE_QP_SEPARATOR = "::";

export function extractCaseIdAndName(qpParam: string): [number | undefined, string | undefined] {
  const tuple = qpParam.split(CASE_QP_SEPARATOR);
  const caseId = tuple[0];
  const caseName = tuple[1];
  return [parseSafeInt(caseId), caseName];
}

export function validateNewFileName(oldName: string, newName: string): string {
  const regex = /[/\u2014\u2013]+/;
  const oldExtension = oldName.substr(oldName.lastIndexOf("."));
  const newExtension = newName.substr(newName.lastIndexOf("."));
  const filePart = newName.substr(0, newName.lastIndexOf("."));
  if (oldExtension !== newExtension) {
    return `File extension must be ${oldExtension}`;
  } else if (regex.test(newName)) {
    return "File name cannot contain / or —";
  } else if (!filePart.trim().length) {
    return "File name cannot be empty";
  }

  return "";
}

interface addValidationProps {
  draftMode: boolean;
  descriptions: string[];
  subtypes: string[];
  field?: FormField;
}

interface addValidationError {
  message: string;
  allowAdd: boolean;
}

export const validateAddRule = (props: addValidationProps): addValidationError => {
  if (!props.draftMode) {
    return {
      message: "Please start a draft",
      allowAdd: false,
    };
  }
  if (props.descriptions.length > 1) {
    return {
      message: "Please select a single description",
      allowAdd: false,
    };
  }
  if (props.subtypes.length > 1) {
    return {
      message: "Please select a single subtype",
      allowAdd: false,
    };
  }
  return {
    message: "",
    allowAdd: true,
  };
};

export function canApplyDate(qpParam: string, date: Date | undefined, searchParams: URLSearchParams): boolean {
  // we're clearing out the date, I'll allow it
  if (!date && searchParams.get(qpParam)) {
    return true;
  }
  // we don't have a fully formed date yet
  else if (!date || isNaN(date.valueOf())) {
    return false;
  }
  // -2208970800000 is the value of 01/01/1900
  // we don't want to trigger the change if the user is in the process of typing the year
  // and they're only at year 0002, 0020 or 0200 for example
  else if (date.valueOf() < -2208970800000) {
    return false;
  }
  // we have an acceptable date to apply
  else {
    return true;
  }
}

export enum COVERSHEET_PROPS {
  DATE = "coversheet_date",
  APPROVED_DATE = "coversheet_approved_date",
  APPROVED_BY = "coversheet_approved_by",
}

export const categoryDateRange = (finding: CategoryFindingSummary): string => {
  return finding.minDate === finding.maxDate
    ? displayDateLoose(finding.minDate)
    : displayDateLoose(finding.minDate) + " - " + displayDateLoose(finding.maxDate);
};

export const displayOutcomeStatus = (
  outcomeStatus: OutcomeStatus | undefined | null,
  sbrEnabled?: boolean | undefined,
  lensOutcomeStatusConfigs?: LensOutcomeStatusConfigurationView[]
): string => {
  if (lensOutcomeStatusConfigs) {
    const outcomeStatusConfig = lensOutcomeStatusConfigs.find((x) => x.outcomeStatus === outcomeStatus);
    if (outcomeStatusConfig) {
      return outcomeStatusConfig.customName;
    }
  }
  return outcomeStatus === undefined || outcomeStatus === OutcomeStatus.Unreviewed
    ? sbrEnabled
      ? "No Outcome"
      : "Review Required"
    : outcomeStatus === OutcomeStatus.Valid
      ? "Valid"
      : outcomeStatus === OutcomeStatus.MissingInformation
        ? "Information Missing"
        : outcomeStatus === OutcomeStatus.DiscrepantInformation
          ? "Information Discrepancy"
          : outcomeStatus === OutcomeStatus.PendingRecords
            ? "Pending Records"
            : outcomeStatus === OutcomeStatus.NotApplicable
              ? "Not Applicable"
              : "Rejected";
};

export const displayValidationStatus = (
  status: ValidationStatus | undefined,
  sbrEnabled?: boolean | undefined
): string => {
  return status === undefined || status === ValidationStatus.Unvalidated
    ? sbrEnabled
      ? "-"
      : "QA Required"
    : status === ValidationStatus.Approved
      ? "QA Approved"
      : status === ValidationStatus.Issue
        ? "QA Issue"
        : "QA Not Applicable";
};

export const displayCaseOutcomeStatus = (status: OverallOutcomeStatus): string | undefined => {
  switch (status) {
    case OverallOutcomeStatus.Valid:
      return "Complete";
    case OverallOutcomeStatus.Pending:
      return "Pending";
    case OverallOutcomeStatus.Issue:
      return "Deficient";
    default:
      return undefined;
  }
};

export const displayCellType = (cellType: CellType): string => {
  switch (cellType) {
    case CellType.MultiSelect:
      return "Multi Select";
    case CellType.Boolean:
      return "Yes/No";
    case CellType.Date:
      return "Date";
    case CellType.Float:
      return "Decimal";
    case CellType.Integer:
      return "Whole Number";
    case CellType.Select:
      return "Select";
    case CellType.Text:
      return "Text";
    default:
      return "Unknown";
  }
};

export function isValidPrintableASCII(str: string): boolean {
  return /^[ -~]*$/.test(str);
}

export function pageFromIntakePageKey(intakePageNumKey: string): number {
  const split = intakePageNumKey.split(":");
  const pageNum = parseSafeInt(split[1]);
  return pageNum === undefined ? 1 : pageNum;
}

export enum BT_PROPS {
  UNLOCK_DATE = "bt_intake_unlock_date",
}

export function getCaseLockInfo(
  caseProperties: CustomCaseProperty[],
  lensCase: Case
): {
  locked: boolean;
  unlockDate: string | undefined;
} {
  const caseUnlockDate = lensCase.properties.find((p) => p.key === BT_PROPS.UNLOCK_DATE)?.value;
  return {
    locked:
      !!caseUnlockDate &&
      !!caseProperties.find((cp) => cp.key === BT_PROPS.UNLOCK_DATE) &&
      Date.now() < parseISO(caseUnlockDate).getTime(),
    unlockDate: caseUnlockDate,
  };
}

export function isNumber(num: number | undefined | null): num is number {
  return typeof num === "number";
}

export function isNotUndefined<T>(val: T | undefined): val is T {
  return val !== undefined;
}

export function isNotNull<T>(val: T | null): val is T {
  return val !== null;
}

export function isNotNullish<T>(val: T | null | undefined): val is T {
  return val !== undefined && val !== null;
}

function isCellType(obj: any): obj is Cell {
  return (
    obj &&
    typeof obj === "object" &&
    "id" in obj &&
    typeof obj.id === "number" &&
    "fieldId" in obj &&
    typeof obj.fieldId === "number" &&
    "name" in obj &&
    typeof obj.name === "string" &&
    "type" in obj &&
    Object.values(CellType).includes(obj.type) &&
    "selectOptions" in obj &&
    ((Array.isArray(obj.selectOptions) && obj.selectOptions.every((option: any) => typeof option === "string")) ||
      obj.selectOptions === null) &&
    "optional" in obj &&
    typeof obj.optional === "boolean" &&
    "default" in obj &&
    "rank" in obj &&
    typeof obj.rank === "number" &&
    "key" in obj &&
    (Object.values(CellKey).includes(obj.key) || obj.key === null) &&
    "exportExclude" in obj &&
    typeof obj.exportExclude === "boolean" &&
    "systemName" in obj &&
    typeof obj.systemName === "string" &&
    "description" in obj &&
    (typeof obj.description === "string" || obj.description === null)
  );
}

function isFormField(obj: any): obj is FormField {
  return (
    obj &&
    typeof obj === "object" &&
    "id" in obj &&
    typeof obj.id === "number" &&
    "name" in obj &&
    typeof obj.name === "string" &&
    "prompt" in obj &&
    typeof obj.prompt === "string" &&
    "repeating" in obj &&
    typeof obj.repeating === "boolean" &&
    "promptYesNo" in obj &&
    typeof obj.promptYesNo === "boolean" &&
    "required" in obj &&
    typeof obj.required === "boolean" &&
    "rank" in obj &&
    typeof obj.rank === "number" &&
    "cells" in obj &&
    Array.isArray(obj.cells) &&
    obj.cells.every((cell: any) => isCellType(cell)) &&
    "key" in obj &&
    (Object.values(FieldKey).includes(obj.key) || obj.key === null) &&
    "exportExclude" in obj &&
    typeof obj.exportExclude === "boolean" &&
    (typeof obj.sectionId === "number" || obj.sectionId === null) &&
    "reviewConfigId" in obj &&
    (typeof obj.reviewConfigId === "number" || obj.reviewConfigId === null) &&
    "outlinePrefix" in obj &&
    (typeof obj.outlinePrefix === "string" || obj.outlinePrefix === null) &&
    "description" in obj &&
    (typeof obj.description === "string" || obj.description === null) &&
    (obj.linkedLensRuleDescriptions?.every((description: any) => typeof description === "string") ?? true) &&
    (obj.linkedLensRuleSubtypes?.every((subtype: any) => typeof subtype === "string") ?? true)
  );
}

function isCellAiConfigs(obj: any): obj is CellAiConfigView[] {
  return (
    Array.isArray(obj) &&
    obj.every(
      (config) =>
        (config.additionalPrompt === null || typeof config.additionalPrompt === "string") &&
        typeof config.aiExclude === "boolean" &&
        (config.alternateName === null || typeof config.alternateName === "string") &&
        typeof config.cellId === "number" &&
        (config.valueLast === null || typeof config.valueLast === "boolean")
    )
  );
}

function isFieldAiConfigExampleSet(obj: any): obj is FieldAiConfigExampleSetInput {
  return (
    typeof obj.example === "string" &&
    Array.isArray(obj.targets) &&
    obj.targets.every((target: any) => typeof target === "string")
  );
}

function isFieldAiConfigExamples(obj: any): obj is FieldAiConfigExamples {
  return (
    (obj.byLensRuleTypeId === null ||
      (Array.isArray(obj.byLensRuleTypeId) && obj.byLensRuleTypeId.every(isFieldAiConfigExampleSet))) &&
    (obj.byRecordType === null ||
      (Array.isArray(obj.byRecordType) && obj.byRecordType.every(isFieldAiConfigExampleSet))) &&
    (obj.default === null || typeof obj.default === "string")
  );
}

function isCustomPropertyQuery(obj: any): obj is CustomPropertyQuery {
  return (
    (obj.exact === null || typeof obj.exact === "boolean") &&
    (obj.excludeValue === null || typeof obj.excludeValue === "boolean") &&
    (obj.key === null || typeof obj.key === "string") &&
    (obj.presence === null || Object.keys(FieldPresence).includes(obj.presence)) &&
    (obj.value === null || typeof obj.value === "string")
  );
}

function isFieldAiConfigFilters(obj: any): obj is FieldAiConfigFilters {
  return (
    (obj.clusterTypes === null ||
      (Array.isArray(obj.clusterTypes) &&
        obj.clusterTypes.every((type: any) => Object.values(ClusterType).includes(type)))) &&
    (obj.documentPropertyQueries === null ||
      (Array.isArray(obj.documentPropertyQueries) && obj.documentPropertyQueries.every(isCustomPropertyQuery))) &&
    (obj.includeNegated === null || typeof obj.includeNegated === "boolean") &&
    (obj.notClusterTypes === null ||
      (Array.isArray(obj.notClusterTypes) &&
        obj.notClusterTypes.every((type: any) => Object.values(ClusterType).includes(type)))) &&
    (obj.notPageLabels === null || Array.isArray(obj.notPageLabels)) &&
    (obj.notPageTypes === null ||
      (Array.isArray(obj.notPageTypes) &&
        obj.notPageTypes.every((type: any) => Object.values(PageType).includes(type)))) &&
    (obj.pageLabels === null || Array.isArray(obj.pageLabels)) &&
    (obj.pageTypes === null ||
      (Array.isArray(obj.pageTypes) && obj.pageTypes.every((type: any) => Object.values(PageType).includes(type)))) &&
    (obj.recommendedOnly === null || typeof obj.recommendedOnly === "boolean") &&
    (obj.ruleIds === null || Array.isArray(obj.ruleIds))
  );
}

export function isFieldAiConfig(obj: any): obj is FieldAiConfigView {
  return (
    (obj.alternateName === null || typeof obj.alternateName === "string") &&
    (obj.alternatePrompt === null || typeof obj.alternatePrompt === "string") &&
    (obj.examples === null || isFieldAiConfigExamples(obj.examples)) &&
    (typeof obj.fieldId === "number" || obj.fieldId === undefined) &&
    typeof obj.fillNotes === "boolean" &&
    (obj.inlineFilters === null || isFieldAiConfigFilters(obj.inlineFilters)) &&
    typeof obj.inputMode === "string" &&
    Object.values(FieldAiConfigInputMode).includes(obj.inputMode) &&
    typeof obj.lensHits === "boolean" &&
    (obj.maxContiguousPages === null || typeof obj.maxContiguousPages === "number") &&
    typeof obj.modelMode === "string" &&
    Object.values(FieldAiConfigModelMode).includes(obj.modelMode) &&
    typeof obj.multiResponse === "boolean" &&
    typeof obj.useReviewConfig === "boolean" &&
    (typeof obj.attributionMode === "string" || !obj.attributionMode)
  );
}

export function isValidJSONFormImportObject(
  obj: any
): obj is { field: FormField; cellAiConfigs: CellAiConfigView[]; fieldAiConfig: FieldAiConfigView } {
  return (
    isFormField(obj.field) &&
    isCellAiConfigs(obj.cellAIConfigs) &&
    (!obj.fieldAIConfig || isFieldAiConfig(obj.fieldAIConfig))
  );
}

export function copyAIFieldConfig(obj: FieldAiConfigView): string {
  const { fieldId, ...rest } = obj;
  return JSON.stringify(rest);
}

export type NewFormField =
  | "name"
  | "prompt"
  | "repeating"
  | "promptYesNo"
  | "required"
  | "key"
  | "exportExclude"
  | "reviewConfigId"
  | "description"
  | "groupByCellId"
  | "analyticsExclude";

export function getFormFieldChanged(field: FormField | undefined, values: Record<NewFormField, any>) {
  return (
    values.name !== field?.name ||
    values.prompt !== field?.prompt ||
    values.repeating !== field?.repeating ||
    values.promptYesNo !== field?.promptYesNo ||
    values.required !== field?.required ||
    values.key !== field?.key ||
    values.exportExclude !== field?.exportExclude ||
    values.reviewConfigId !== field?.reviewConfigId ||
    values.description !== field?.description ||
    values.groupByCellId !== field?.groupByCellId ||
    values.analyticsExclude !== field?.analyticsExclude
  );
}

export interface PropertyCell {
  prop: string;
  description?: string;
  value?: string | ReactElement;
  textValue?: string;
  hidden?: boolean;
  notCopyable?: boolean;
}

export interface PropertyDisplayGroup {
  name: string;
  description?: string;
  cells: PropertyCell[];
  canEdit: boolean;
}
